import { useEffect, useMemo, useState } from 'react'
import { ImageContext, LinkContext } from '@overdose/components'
import {
  Hydrate,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { NextPage } from 'next'
import { SessionProvider } from 'next-auth/react'
import type { AppProps } from 'next/app'
// eslint-disable-next-line camelcase
import { Archivo_Black, Figtree, Roboto } from 'next/font/google'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { SiteCodes } from 'shared-types'
import { CustomImage } from '~/components/CustomImage/CustomImage'
import { CustomLink } from '~/components/CustomLink'
import ZendeskLauncher from '~/components/ZendeskLauncher'
import { initAlgoliaClient } from '~/hooks'
import '~/styles/globals.scss'

import { useUserInteraction } from '~/hooks/useUserInteraction'
import { ContentStackLivePreviewWebComponent } from '~/lib'

import {
  CartDrawerProvider,
  CompareProductsProvider,
  CustomerListsProvider,
  PageProvider,
  ToastProvider,
  UserDeliveryPostcodeProvider,
  UserLocationProvider,
  UserPreferredStoreProvider,
  GoogleRecaptchaProvider,
  ZendeskProvider,
  BrazeProvider,
} from '../providers'

const figtree = Figtree({
  subsets: ['latin'],
  display: 'swap',
  weight: ['400', '500', '600', '700', '800'],
})

const archivoBlack = Archivo_Black({
  subsets: ['latin'],
  display: 'swap',
  weight: '400',
})

const roboto = Roboto({
  subsets: ['latin'],
  display: 'swap',
  weight: ['400', '500', '700'],
})

type AppPropsWithLayout = AppProps & {
  Component: NextPage
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const { session } = pageProps

  const gtagId = pageProps?.page?.gtagId

  const isRTM = pageProps?.page?.siteCode === SiteCodes.RTM

  const zendeskEnv = useMemo(() => {
    const siteCode = pageProps?.page?.siteCode

    if (siteCode === SiteCodes.RTM) {
      return process.env.NEXT_PUBLIC_ZENDESK_ID_RTM
    }

    if (siteCode === SiteCodes.JaycarNz) {
      return process.env.NEXT_PUBLIC_ZENDESK_ID_NZ
    }

    return process.env.NEXT_PUBLIC_ZENDESK_ID_AU
  }, [pageProps?.page?.siteCode])

  const router = useRouter()

  const evergageEnvironment = process.env.NEXT_PUBLIC_EVERGAGE_ENVIRONMENT
  const evergageScriptPath = `/beacon/${evergageEnvironment}/scripts/evergage.min.js`
  const evergageHost = 'cdn.evgnet.com'

  const forterSiteId = process.env.NEXT_PUBLIC_FORTER_SITE_ID

  initAlgoliaClient()

  const { userHasInteracted } = useUserInteraction()

  useEffect(() => {
    router.prefetch = async () => {}
  }, [router])

  const [queryClient] = useState(() => {
    return new QueryClient({
      queryCache: new QueryCache({
        onError() {
          console.error('QUERY_CLIENT: error.default')
        },
      }),
      mutationCache: new MutationCache({
        onError(_error, _variables, _context, mutation) {
          if (mutation.meta?.notificationKey) {
            console.error(
              `error.${mutation.meta.notificationKey}`,
              mutation.meta.notificationKey
            )
          } else {
            console.error('QUERY_CLIENT: error.default')
          }
        },
      }),
      defaultOptions: {
        mutations: {
          retry: 3,
          retryDelay: (failureCount) => {
            return Number('500') * Number('2') ** failureCount
          },
        },
      },
    })
  })

  return (
    <div className={`theme-${pageProps?.page?.brand || 'jc'}`}>
      <SessionProvider
        session={session}
        basePath='/fe/api/auth'
        refetchOnWindowFocus={false}>
        <ContentStackLivePreviewWebComponent />
        <LinkContext.Provider value={CustomLink}>
          <ImageContext.Provider value={CustomImage}>
            <QueryClientProvider client={queryClient}>
              <Head>
                <meta
                  name='viewport'
                  content='minimum-scale=1, initial-scale=1, width=device-width'
                  key='viewport'
                />
                {isRTM ? (
                  <>
                    <link
                      rel='shortcut icon'
                      type='image/x-icon'
                      media='all'
                      href='/theme-rtm/favicon.ico'
                    />
                    <link rel='apple-touch-icon' sizes='180x180' href='/' />
                    <link
                      rel='icon'
                      sizes='32x32'
                      href='/theme-rtm/icons/favicon-32x32.png'
                    />
                    <link
                      rel='icon'
                      sizes='16x16'
                      href='/theme-rtm/icons/favicon-16x16.png'
                    />
                    <link
                      rel='manifest'
                      href='/theme-rtm/icons/site.webmanifest'
                    />
                    <link
                      rel='mask-icon'
                      href='/theme-rtm/icons/safari-pinned-tab.svg'
                      color='0c254c'
                    />
                  </>
                ) : (
                  <>
                    <link
                      rel='shortcut icon'
                      type='image/x-icon'
                      media='all'
                      href='/favicon.ico'
                    />
                    <link rel='apple-touch-icon' sizes='180x180' href='/' />
                    <link
                      rel='icon'
                      sizes='32x32'
                      href='/icons/favicon-32x32.png'
                    />
                    <link
                      rel='icon'
                      sizes='16x16'
                      href='/icons/favicon-16x16.png'
                    />
                    <link rel='manifest' href='/icons/site.webmanifest' />
                    <link
                      rel='mask-icon'
                      href='/icons/safari-pinned-tab.svg'
                      color='0c254c'
                    />
                  </>
                )}

                {/* Zendesk widget preloads */}
                {zendeskEnv && (
                  <>
                    <link
                      rel='dns-prefetch'
                      href='https://static.zdassets.com'
                      key='zdassets-prefetch'
                    />
                    <link
                      rel='preconnect'
                      href='https://static.zdassets.com'
                      key='zdassets-preconnect'
                    />
                    <link
                      rel='prefetch'
                      href={`https://static.zdassets.com/ekr/snippet.js?key=${zendeskEnv}`}
                      as='script'
                      key='zdassets-snippet-prefetch'
                    />
                  </>
                )}
              </Head>

              {/* Forter Script */}
              {forterSiteId && (
                <Script
                  id='forter-script'
                  type='text/javascript'
                  strategy='lazyOnload'
                  dangerouslySetInnerHTML={{
                    __html: `(function () {
											var merchantConfig = {
													csp: false
											};

											var siteId = "${forterSiteId}";
							function t(t,n){for(var e=t.split(""),r=0;r<e.length;++r)e[r]=String.fromCharCode(e[r].charCodeAt(0)+n);return e.join("")}function n(n){return t(n,-L).replace(/%SN%/g,siteId)}function e(){var t="no"+"op"+"fn",n="g"+"a",e="n"+"ame";return window[n]&&window[n][e]===t}function r(){return!(!navigator.brave||"function"!=typeof navigator.brave.isBrave)}function o(){return document.currentScript&&document.currentScript.src}function i(t){try{if("number"==typeof t&&window.location&&window.location.pathname){for(var n=window.location.pathname.split("/"),e=[],r=0;r<=Math.min(n.length-1,Math.abs(t));r++)e.push(n[r]);return e.join("/")||"/"}}catch(t){}return"/"}function c(t){try{G.ex=t,e()&&-1===G.ex.indexOf(B.uB)&&(G.ex+=B.uB),r()&&-1===G.ex.indexOf(B.uBr)&&(G.ex+=B.uBr),o()&&-1===G.ex.indexOf(B.nIL)&&(G.ex+=B.nIL),window.ftr__snp_cwc||(G.ex+=B.s),q(G)}catch(t){}}function a(t,n){function e(o){try{o.blockedURI===t&&(n(),document.removeEventListener(r,e))}catch(t){document.removeEventListener(r,e)}}var r="securitypolicyviolation";document.addEventListener(r,e),setTimeout(function(){document.removeEventListener(r,e)},2*60*1e3)}function u(t,n,e,r){var o=!1;t="https://"+t,a(t,function(){r(!0),o=!0});var i=document.createElement("script");i.onerror=function(){if(!o)try{r(!1),o=!0}catch(t){}},i.onload=e,i.type="text/javascript",i.id="ftr__script",i.async=!0,i.src=t;var c=document.getElementsByTagName("script")[0];c.parentNode.insertBefore(i,c)}function f(t,n,e,r){var o=!1,i=new XMLHttpRequest;if(a("https:"+t,function(){e(new Error("CSP Violation"),!0),o=!0}),"//"===t.slice(0,2)&&(t="https:"+t),"withCredentials"in i)i.open("GET",t,!0);else{if("undefined"==typeof XDomainRequest)return;i=new XDomainRequest,i.open("GET",t)}Object.keys(r).forEach(function(t){i.setRequestHeader(t,r[t])}),i.onload=function(){"function"==typeof n&&n(i)},i.onerror=function(t){if("function"==typeof e&&!o)try{e(t,!1),o=!0}catch(t){}},i.onprogress=function(){},i.ontimeout=function(){"function"==typeof e&&e("tim"+"eo"+"ut",!1)},setTimeout(function(){i.send()},0)}function d(t,siteId,n){function e(t){var n=t.toString(16);return n.length%2?"0"+n:n}function r(t){if(t<=0)return"";for(var n="0123456789abcdef",e="",r=0;r<t;r++)e+=n[Math.floor(Math.random()*n.length)];return e}function o(t){for(var n="",r=0;r<t.length;r++)n+=e(t.charCodeAt(r));return n}function i(t){for(var n=t.split(""),e=0;e<n.length;++e)n[e]=String.fromCharCode(255^n[e].charCodeAt(0));return n.join("")}n=n?"1":"0";var c=[];return c.push(t),c.push(siteId),c.push(n),function(t){var n=40,e="";return t.length<n/2&&(e=","+r(n/2-t.length-1)),o(i(t+e))}(c.join(","))}function s(){function t(){b&&(z(B.dUAL),setTimeout(w,I,B.dUAL))}function n(t,n){c(n?B.uAS+B.uF+B.cP:B.uAS+B.uF)}window.ftr__fdad(t,n)}function w(t){try{var n=t===B.uDF?E:b;if(!n)return;u(n,void 0,function(){try{$(),c(t+B.uS)}catch(t){}},function(n){try{$(),G.td=1*new Date-G.ts,c(n?t+B.uF+B.cP:t+B.uF),t===B.uDF&&s()}catch(t){c(B.eUoe)}})}catch(n){c(t+B.eTlu)}}var h="22g5o{trgtes|:f51forxgiurqw1qhw2vwdwxv",v="fort",m="erTo",l="ken";window.ftr__config={m:merchantConfig,s:"21",si:siteId};var p=!1,g=v+m+l,_=400*24*60,y=10,T={write:function(t,n,e,r){void 0===r&&(r=!0);var o=0;window.ftr__config&&window.ftr__config.m&&window.ftr__config.m.ckDepth&&(o=window.ftr__config.m.ckDepth);var c,a,u=i(o);if(e?(c=new Date,c.setTime(c.getTime()+60*e*1e3),a="; expires="+c.toGMTString()):a="",!r)return void(document.cookie=escape(t)+"="+escape(n)+a+"; path="+u);for(var f=1,d=document.domain.split("."),s=y,w=!0;w&&d.length>=f&&s>0;){var h=d.slice(-f).join(".");document.cookie=escape(t)+"="+escape(n)+a+"; path="+u+"; domain="+h;var v=T.read(t);null!=v&&v==n||(h="."+h,document.cookie=escape(t)+"="+escape(n)+a+"; path="+u+"; domain="+h),w=-1===document.cookie.indexOf(t+"="+n),f++,s--}},read:function(t){var n=null;try{for(var e=escape(t)+"=",r=document.cookie.split(";"),o=32,i=0;i<r.length;i++){for(var c=r[i];c.charCodeAt(0)===o;)c=c.substring(1,c.length);0===c.indexOf(e)&&(n=unescape(c.substring(e.length,c.length)))}}finally{return n}}},S=window.ftr__config.s;S+="ck";var x=function(t){var n=!1,e=null,r=function(){try{if(!e||!n)return;e.remove&&"function"==typeof e.remove?e.remove():document.head.removeChild(e),n=!1}catch(t){}};document.head&&(!function(){e=document.createElement("link"),e.setAttribute("rel","pre"+"con"+"nect"),e.setAttribute("cros"+"sori"+"gin","anonymous"),e.onload=r,e.onerror=r,e.setAttribute("href",t),document.head.appendChild(e),n=!0}(),setTimeout(r,3e3))},L=3,k=n(h||"22g5o{trgtes|:f51forxgiurqw1qhw2vwdwxv"),A=t("[0Uhtxhvw0LG",-L),U=t("[0Fruuhodwlrq0LG",-L),D=t("Li0Qrqh0Pdwfk",-L),b,C="fgq71iruwhu1frp",E=n("(VQ(1"+C+"2vq2(VQ(2vfulsw1mv"),F=n("(VQ(1"+C+"2vqV2(VQ(2vfulsw1mv"),I=10;window.ftr__startScriptLoad=1*new Date;var V=function(t){var n="ft"+"r:tok"+"enR"+"eady";window.ftr__tt&&clearTimeout(window.ftr__tt),window.ftr__tt=setTimeout(function(){try{delete window.ftr__tt,t+="_tt";var e=document.createEvent("Event");e.initEvent(n,!1,!1),e.detail=t,document.dispatchEvent(e)}catch(t){}},1e3)},q=function(t){var n=function(t){return t||""},e=n(t.id)+"_"+n(t.ts)+"_"+n(t.td)+"_"+n(t.ex)+"_"+n(S),r=_;!isNaN(window.ftr__config.m.ckTTL)&&window.ftr__config.m.ckTTL&&(r=window.ftr__config.m.ckTTL),T.write(g,e,r,!0),V(e),window.ftr__gt=e},R=function(){var t=T.read(g)||"",n=t.split("_"),e=function(t){return n[t]||void 0};return{id:e(0),ts:e(1),td:e(2),ex:e(3),vr:e(4)}},M=function(){for(var t={},n="fgu",e=[],r=0;r<256;r++)e[r]=(r<16?"0":"")+r.toString(16);var o=function(t,n,r,o,i){var c=i?"-":"";return e[255&t]+e[t>>8&255]+e[t>>16&255]+e[t>>24&255]+c+e[255&n]+e[n>>8&255]+c+e[n>>16&15|64]+e[n>>24&255]+c+e[63&r|128]+e[r>>8&255]+c+e[r>>16&255]+e[r>>24&255]+e[255&o]+e[o>>8&255]+e[o>>16&255]+e[o>>24&255]},i=function(){if(window.Uint32Array&&window.crypto&&window.crypto.getRandomValues){var t=new window.Uint32Array(4);return window.crypto.getRandomValues(t),{d0:t[0],d1:t[1],d2:t[2],d3:t[3]}}return{d0:4294967296*Math.random()>>>0,d1:4294967296*Math.random()>>>0,d2:4294967296*Math.random()>>>0,d3:4294967296*Math.random()>>>0}},c=function(){var t="",n=function(t,n){for(var e="",r=t;r>0;--r)e+=n.charAt(1e3*Math.random()%n.length);return e};return t+=n(2,"0123456789"),t+=n(1,"123456789"),t+=n(8,"0123456789")};return t.safeGenerateNoDash=function(){try{var t=i();return o(t.d0,t.d1,t.d2,t.d3,!1)}catch(t){try{return n+c()}catch(t){}}},t.isValidNumericalToken=function(t){return t&&t.toString().length<=11&&t.length>=9&&parseInt(t,10).toString().length<=11&&parseInt(t,10).toString().length>=9},t.isValidUUIDToken=function(t){return t&&32===t.toString().length&&/^[a-z0-9]+$/.test(t)},t.isValidFGUToken=function(t){return 0==t.indexOf(n)&&t.length>=12},t}(),B={uDF:"UDF",dUAL:"dUAL",uAS:"UAS",mLd:"1",eTlu:"2",eUoe:"3",uS:"4",uF:"9",tmos:["T5","T10","T15","T30","T60"],tmosSecs:[5,10,15,30,60],bIR:"43",uB:"u",uBr:"b",cP:"c",nIL:"i",s:"s"};try{var G=R();try{G.id&&(M.isValidNumericalToken(G.id)||M.isValidUUIDToken(G.id)||M.isValidFGUToken(G.id))?window.ftr__ncd=!1:(G.id=M.safeGenerateNoDash(),window.ftr__ncd=!0),G.ts=window.ftr__startScriptLoad,q(G),window.ftr__snp_cwc=!!T.read(g),window.ftr__snp_cwc||(E=F);for(var N="for"+"ter"+".co"+"m",O="ht"+"tps://c"+"dn9."+N,j="ht"+"tps://"+G.id+"-"+siteId+".cd"+"n."+N,H="http"+"s://cd"+"n3."+N,P=[O,j,H],Q=0;Q<P.length;Q++)x(P[Q]);var X=new Array(B.tmosSecs.length),z=function(t){for(var n=0;n<B.tmosSecs.length;n++)X[n]=setTimeout(c,1e3*B.tmosSecs[n],t+B.tmos[n])},$=function(){for(var t=0;t<B.tmosSecs.length;t++)clearTimeout(X[t])};window.ftr__fdad=function(n,e){if(!p){p=!0;var r={};r[D]=d(window.ftr__config.s,siteId,window.ftr__config.m.csp),f(k,function(e){try{var r=e.getAllResponseHeaders().toLowerCase();if(r.indexOf(U.toLowerCase())>=0){var o=e.getResponseHeader(U);window.ftr__altd2=t(atob(o),-L-1)}if(r.indexOf(A.toLowerCase())<0)return;var i=e.getResponseHeader(A),c=t(atob(i),-L-1);if(c){var a=c.split(":");if(a&&2===a.length){for(var u=a[0],f=a[1],d="",s=0,w=0;s<20;++s)d+=s%3>0&&w<12?siteId.charAt(w++):G.id.charAt(s);var h=f.split(",");if(h.length>1){var v=h[0],m=h[1];b=u+"/"+v+"."+d+"."+m}}}n()}catch(t){}},function(t,n){e&&e(t,n)},r)}},z(B.uDF),setTimeout(w,I,B.uDF)}catch(t){c(B.mLd)}}catch(t){}})();`,
                  }}
                />
              )}

              {/* Google AdWords */}
              {gtagId && (
                <>
                  <Script
                    id='google-analytics'
                    type='text/javascript'
                    strategy='afterInteractive'
                    src={`https://www.googletagmanager.com/gtag/js?id=${gtagId}`}
                  />
                  <Script
                    id='gtag-script'
                    type='text/javascript'
                    strategy='afterInteractive'
                    dangerouslySetInnerHTML={{
                      __html: `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'AW-CONVERSION_ID');`,
                    }}
                  />
                </>
              )}

              {/* Zendesk widget */}
              {userHasInteracted && zendeskEnv && (
                <Script
                  src={`https://static.zdassets.com/ekr/snippet.js?key=${zendeskEnv}`}
                  id='ze-snippet'
                  strategy='lazyOnload'
                  onLoad={() => {
                    window.zE('messenger:set', 'zIndex', Number('60'))
                  }}
                />
              )}

              {/* BugHerd */}
              {userHasInteracted && process.env.NEXT_PUBLIC_BUGHERD_API_KEY && (
                <Script
                  src={`https://www.bugherd.com/sidebarv2.js?apikey=${process.env.NEXT_PUBLIC_BUGHERD_API_KEY}`}
                  id='bugherd-script-loader'
                  strategy='lazyOnload'
                />
              )}

              {/* Evergage */}
              {userHasInteracted && evergageEnvironment && (
                <Script src={`//${evergageHost}${evergageScriptPath}`} />
              )}

              {/* eslint-disable-next-line react/no-unknown-property */}
              <style jsx global>{`
                :root {
                  --font-family-primary: ${isRTM
                    ? roboto.style.fontFamily
                    : figtree.style.fontFamily};
                  --font-family-secondary: ${isRTM
                    ? archivoBlack.style.fontFamily
                    : roboto.style.fontFamily};
                  --font-family-heading: ${isRTM
                    ? archivoBlack.style.fontFamily
                    : figtree.style.fontFamily};
                }
              `}</style>

              <BrazeProvider>
                <GoogleRecaptchaProvider
                  isRecaptchaDomain={pageProps?.page?.isRecaptchaDomain}>
                  <PageProvider {...pageProps?.page}>
                    <ZendeskProvider>
                      <ZendeskLauncher />
                      <ToastProvider>
                        <CartDrawerProvider>
                          <Hydrate state={pageProps.dehydratedState}>
                            <UserLocationProvider>
                              <UserDeliveryPostcodeProvider>
                                <UserPreferredStoreProvider>
                                  <CustomerListsProvider>
                                    <CompareProductsProvider>
                                      <Component {...pageProps} />
                                    </CompareProductsProvider>
                                  </CustomerListsProvider>
                                </UserPreferredStoreProvider>
                              </UserDeliveryPostcodeProvider>
                            </UserLocationProvider>
                          </Hydrate>
                        </CartDrawerProvider>
                      </ToastProvider>
                    </ZendeskProvider>
                  </PageProvider>
                </GoogleRecaptchaProvider>
              </BrazeProvider>
              <ReactQueryDevtools />
            </QueryClientProvider>
          </ImageContext.Provider>
        </LinkContext.Provider>
      </SessionProvider>
    </div>
  )
}

export default MyApp
